/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { title } from "./Functions";

export interface StateMapping {
    name: string;
    abbrev: string;
}

export const STATES_MAPPING: StateMapping[] = [
    { name: "Alabama", abbrev: "AL" },
    { name: "Alaska", abbrev: "AK" },
    { name: "Arizona", abbrev: "AZ" },
    { name: "Arkansas", abbrev: "AR" },
    { name: "California", abbrev: "CA" },
    { name: "Colorado", abbrev: "CO" },
    { name: "Connecticut", abbrev: "CT" },
    { name: "Delaware", abbrev: "DE" },
    { name: "District of Columbia", abbrev: "DC" },
    { name: "Florida", abbrev: "FL" },
    { name: "Georgia", abbrev: "GA" },
    { name: "Hawaii", abbrev: "HI" },
    { name: "Idaho", abbrev: "ID" },
    { name: "Illinois", abbrev: "IL" },
    { name: "Indiana", abbrev: "IN" },
    { name: "Iowa", abbrev: "IA" },
    { name: "Kansas", abbrev: "KS" },
    { name: "Kentucky", abbrev: "KY" },
    { name: "Louisiana", abbrev: "LA" },
    { name: "Maine", abbrev: "ME" },
    { name: "Maryland", abbrev: "MD" },
    { name: "Massachusetts", abbrev: "MA" },
    { name: "Michigan", abbrev: "MI" },
    { name: "Minnesota", abbrev: "MN" },
    { name: "Mississippi", abbrev: "MS" },
    { name: "Missouri", abbrev: "MO" },
    { name: "Montana", abbrev: "MT" },
    { name: "Nebraska", abbrev: "NE" },
    { name: "Nevada", abbrev: "NV" },
    { name: "New Hampshire", abbrev: "NH" },
    { name: "New Jersey", abbrev: "NJ" },
    { name: "New Mexico", abbrev: "NM" },
    { name: "New York", abbrev: "NY" },
    { name: "North Carolina", abbrev: "NC" },
    { name: "North Dakota", abbrev: "ND" },
    { name: "Ohio", abbrev: "OH" },
    { name: "Oklahoma", abbrev: "OK" },
    { name: "Oregon", abbrev: "OR" },
    { name: "Pennsylvania", abbrev: "PA" },
    { name: "Rhode Island", abbrev: "RI" },
    { name: "South Carolina", abbrev: "SC" },
    { name: "South Dakota", abbrev: "SD" },
    { name: "Tennessee", abbrev: "TN" },
    { name: "Texas", abbrev: "TX" },
    { name: "Utah", abbrev: "UT" },
    { name: "Vermont", abbrev: "VT" },
    { name: "Virginia", abbrev: "VA" },
    { name: "Washington", abbrev: "WA" },
    { name: "West Virginia", abbrev: "WV" },
    { name: "Wisconsin", abbrev: "WI" },
    { name: "Wyoming", abbrev: "WY" },
];

export const workOrdersStatusTitles: {
    UN: string;
    IP: string;
    SB: string;
    CM: string;
    ALL: string;
} = {
    UN: "Unassigned",
    IP: "In Progress",
    SB: "Submitted",
    CM: "Completed",
    ALL: "All",
};

export const BLANK = {};

export enum AssetType {
    TOOL = 0,
    PPE = 1,
    CONSUMABLE = 2,
    IT_ASSET = 3,
}

export enum Condition {
    EXCELLENT = 0,
    GOOD = 1,
    FAIR = 2,
    POOR = 3,
    FAILED = 4,
    OBSOLETE = 5,
    FULL = 10,
    OPEN = 11,
    RESTOCK_NEEDED = 12,
    EMPTY = 13,
}

export enum DisposalReason {
    IN_SERVICE = 0,
    BROKEN = 1,
    OBSOLETE = 2,
    CONSUMED = 3,
}

export enum DisposalMethod {
    IN_SERVICE = 0,
    RETAINED = 2,
    TRASH = 3,
    RECYCLE = 4,
    SPECIAL = 5,
}

// let name: string = enumToName<Condition>(Condition.EXCELLENT);

export function conditionToName(item: Condition): string {
    return title(Condition[item].toString().replaceAll("_", " "));
}

export function conditionColor(item: Condition): "success" | "warning" | "error" | "info" {
    let mapping = {
        [Condition.EXCELLENT]: "success",
        [Condition.GOOD]: "info",
        [Condition.FAIR]: "warning",
        [Condition.POOR]: "error",
        [Condition.FAILED]: "error",
        [Condition.OBSOLETE]: "error",
        [Condition.FULL]: "success",
        [Condition.OPEN]: "info",
        [Condition.RESTOCK_NEEDED]: "warning",
        [Condition.EMPTY]: "error",
    };
    // @ts-ignore
    return mapping[item] + ".main";
}

export type AssetTypeMap = { name: string; value: AssetType };

export const assetTypeMappings: AssetTypeMap[] = [
    { name: "Tool", value: AssetType.TOOL },
    { name: "PPE", value: AssetType.PPE },
    { name: "Consumable", value: AssetType.CONSUMABLE },
    { name: "IT Asset", value: AssetType.IT_ASSET },
];

export function lookupAssetType(assetType: AssetType): AssetTypeMap {
    return assetTypeMappings.filter((assetMapping: AssetTypeMap) => assetMapping.value === assetType)[0];
}

export type ConditionMap = { name: string; value: Condition };

export const conditionMappings: ConditionMap[] = [
    { name: "Excellent", value: Condition.EXCELLENT },
    { name: "Good", value: Condition.GOOD },
    { name: "Fair", value: Condition.FAIR },
    { name: "Poor", value: Condition.POOR },
    { name: "Failed", value: Condition.FAILED },
    { name: "Obsolete", value: Condition.OBSOLETE },
    { name: "Full", value: Condition.FULL },
    { name: "Open", value: Condition.OPEN },
    { name: "Restock Needed", value: Condition.RESTOCK_NEEDED },
    { name: "Empty", value: Condition.EMPTY },
];

export function lookupValidConditionMappings(assetType: AssetType): ConditionMap[] {
    switch (assetType) {
        case AssetType.TOOL: {
            return [
                conditionMappings[0],
                conditionMappings[1],
                conditionMappings[2],
                conditionMappings[3],
                conditionMappings[4],
                conditionMappings[5],
            ];
        }
        case AssetType.CONSUMABLE: {
            return [conditionMappings[6], conditionMappings[7], conditionMappings[8], conditionMappings[9]];
        }
        case AssetType.PPE: {
            return [
                conditionMappings[0],
                conditionMappings[1],
                conditionMappings[2],
                conditionMappings[3],
                conditionMappings[4],
                conditionMappings[5],
            ];
        }
        case AssetType.IT_ASSET: {
            return [
                conditionMappings[0],
                conditionMappings[1],
                conditionMappings[2],
                conditionMappings[3],
                conditionMappings[4],
                conditionMappings[5],
            ];
        }
    }
}

export function lookupCondition(condition: Condition): ConditionMap {
    return conditionMappings.filter((condIter: ConditionMap) => condIter.value === condition)[0];
}

export type DisposalReasonMap = { name: string; value: DisposalReason };

export const disposalReasonMappings: DisposalReasonMap[] = [
    { name: "In Service", value: DisposalReason.IN_SERVICE },
    { name: "Broken", value: DisposalReason.BROKEN },
    { name: "Obsolete", value: DisposalReason.OBSOLETE },
    { name: "Consumed", value: DisposalReason.CONSUMED },
];

export function lookupValidDisposalReasonMappings(assetType: AssetType): DisposalReasonMap[] {
    switch (assetType) {
        case AssetType.TOOL: {
            return [disposalReasonMappings[0], disposalReasonMappings[1], disposalReasonMappings[2]];
        }
        case AssetType.CONSUMABLE: {
            return [disposalReasonMappings[0], disposalReasonMappings[3]];
        }
        case AssetType.PPE: {
            return [disposalReasonMappings[0], disposalReasonMappings[1], disposalReasonMappings[3]];
        }
        case AssetType.IT_ASSET: {
            return [disposalReasonMappings[0], disposalReasonMappings[1], disposalReasonMappings[2]];
        }
    }
}

export function lookupDisposalReason(disposalReason: DisposalReason): DisposalReasonMap {
    return disposalReasonMappings.filter((dispReason: DisposalReasonMap) => dispReason.value === disposalReason)[0];
}

export type DisposalMethodMap = { name: string; value: DisposalMethod };

export const disposalMethodMappings: DisposalMethodMap[] = [
    { name: "In Service", value: DisposalMethod.IN_SERVICE },
    { name: "Retained", value: DisposalMethod.RETAINED },
    { name: "Trash", value: DisposalMethod.TRASH },
    { name: "Recycle", value: DisposalMethod.RECYCLE },
    { name: "Special", value: DisposalMethod.SPECIAL },
];

export function lookupDisposalMethod(disposalMethod: DisposalMethod): DisposalMethodMap {
    return disposalMethodMappings.filter((dispMethod: DisposalMethodMap) => dispMethod.value === disposalMethod)[0];
}

export enum TransferMode {
    INGRESS,
    TRANSFER,
    EGRESS,
    DEFECTIVE,
    ADMIN,
}

export type TransferModeMap = { name: string; value: TransferMode; abbrev: string };

export const transferModeMappings: TransferModeMap[] = [
    { name: "Inventory Ingress", value: TransferMode.INGRESS, abbrev: "Ingress" },
    { name: "Inventory-to-Inventory Transfer", value: TransferMode.TRANSFER, abbrev: "Transfer" },
    { name: "Inventory Egress", value: TransferMode.EGRESS, abbrev: "Egress" },
    { name: "Mark Defective", value: TransferMode.DEFECTIVE, abbrev: "Defective" },
    { name: "Inventory Administration", value: TransferMode.ADMIN, abbrev: "Admin" },
];

export function lookupTransferMode(transferMode: TransferMode): TransferModeMap {
    return transferModeMappings.filter((xferMode: TransferModeMap) => xferMode.value === transferMode)[0];
}

export enum TransferType {
    INGRESS = 0,
    TRANSFER = 1,
    EGRESS = 2,
    DEFECTIVE_WALMART = 3,
    DEFECTIVE_TECHWISE = 4,
    ADMINISTRATIVE_ADD = 5,
    ADMINISTRATIVE_SUBTRACT = 6,
    LOSS = 7,
}

export function lookupTransferType(transferType: TransferType): string {
    const mappings = {
        0: "INGRESS",
        1: "TRANSFER",
        2: "EGRESS",
        3: "DEFECTIVE - RMA",
        4: "DEFECTIVE - LOSS",
        5: "ADMIN - ADD",
        6: "ADMIN - REMOVE",
        7: "DEFECTIVE - LOSS",
    };

    return mappings[transferType as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7];
}

export function lookupTransferTypeColor(transferType: TransferType): "success" | "info" | "warning" | "error" | "primary" {
    const mappings = {
        0: "primary",
        1: "info",
        2: "success",
        3: "warning",
        4: "warning",
        5: "primary",
        6: "primary",
        7: "error",
    };

    return mappings[transferType as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7] as "success" | "info" | "warning" | "error" | "primary";
}

export enum LocationType {
    PERMANENT = 0,
    TECHNICIAN_VAN = 1,
    DEFECTIVE_WALMART = 2,
    DEFECTIVE_TECHWISE = 3,
    LOSS = 4,
}
